import { useEffect } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';

import { FullPageSpinner } from '@/components/FullPageSpinner';
import { useSession } from '@/lib/auth-client';
import { HOME_PATH_BY_ROLE, type Role } from '@shared/schemas/user';

/**
 * Where sign-in lands, and where it does not stay.
 *
 * One place decides what a role sees first, rather than the login form knowing
 * about every portal. It also means a link to `/launch` always takes whoever
 * follows it to their own home, which is what the topbar logo does — and since
 * 11.5 it is the installed app's `start_url`, so "opening Honeycomb" on a
 * phone is this route resolving.
 *
 * **`replace`, always.** This route resolves and leaves. Pushing it would put a
 * page that immediately redirects into the history stack, so pressing Back from
 * the dashboard would land here and be bounced straight forward again — the Back
 * button stops working and nobody can say why.
 */

/**
 * The manifest's shortcuts — a long-press on the installed icon. Employee
 * destinations, because both shortcuts are the crew's ("Today's Jobs", and
 * "Clock In", whose card lives on the dashboard). Any other role ignores `to`
 * and lands home: an admin who pressed Clock In has nothing to clock into,
 * and their own dashboard beats an error.
 */
function destinationFor(role: Role, to: string | null): string {
  if (role === 'employee' && to === 'jobs') return '/employee/jobs';
  if (role === 'employee' && to === 'clock-in') return '/employee';
  return HOME_PATH_BY_ROLE[role];
}

export function LaunchPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { data: session, isPending } = useSession();

  const role = session?.user.role;
  const to = params.get('to');

  useEffect(() => {
    if (!role) return;
    void navigate(destinationFor(role, to), { replace: true });
  }, [role, to, navigate]);

  if (isPending) return <FullPageSpinner label="Signing you in" />;
  if (!session) return <Navigate to="/login" replace />;

  return <FullPageSpinner label="Signing you in" />;
}
